import {
    CalculateEventStatusParams,
    CalculateEventStatusResult,
    InvitationStatus,
    RSVPEventStatus,
    SentSmsStatus,
    SmsStatusDisplay,
} from '../types/Events';
import { INVITATIONS_MAP, USER_REMINDER } from '../constants/event';
import { UserModel } from '../types/User';
import EventContact from '../db/services/EventContact';
import { EVENT_CONTACT_STATUS, EventContactModel } from '../types/EventContact';
import { ContactModel } from '../types/Contacts';
import Contact from '../db/services/Contact';
import { LoggerFunction } from '../types/logger';

async function getEventCardColor(
    currentUserId: string,
    membersWithColors: { user: UserModel; userColor: string }[],
) {
    let eventCardColor: string = '';
    let members = membersWithColors;

    const currentUserEventMember = members.find(
        ({ user }) => user.id === currentUserId,
    );

    // Move current user event member to the beginning
    if (!!currentUserEventMember) {
        const eventMembersWithoutCurrentUser = members.filter(
            (eventMember) =>
                eventMember.user.id !== currentUserEventMember.user.id,
        );

        members = [currentUserEventMember, ...eventMembersWithoutCurrentUser];
    }

    if (members.length) {
        if (!!currentUserEventMember) {
            eventCardColor = currentUserEventMember.userColor;
        } else {
            eventCardColor = members[0].userColor;
        }
    }

    return eventCardColor;
}

function parseInvitations(ownersReminder: boolean, contactsReminder: boolean) {
    let invites = INVITATIONS_MAP.none;

    if (contactsReminder && !ownersReminder) {
        invites = INVITATIONS_MAP.contacts;
    } else if (ownersReminder && !contactsReminder) {
        invites = INVITATIONS_MAP.owners;
    } else if (ownersReminder && contactsReminder) {
        invites = INVITATIONS_MAP.contacts_and_owners;
    }

    return invites;
}

function calculateContactInvitationState(
    sentFlag: boolean,
    sentAt: string,
): InvitationStatus {
    if (!sentFlag && !sentAt) {
        return 'not_sent';
    }

    if (sentFlag && !sentAt) {
        return 'sending';
    }

    if (sentFlag && sentAt) {
        return 'sent';
    }
    return 'not_sent';
}

async function calculateStatus(
    invitations: EventContactModel[],
    totalLength: number,
    contactService: Contact,
    logError: LoggerFunction,
): Promise<RSVPEventStatus | null> {
    if (!invitations.length) {
        return null;
    }

    let contactToShow: ContactModel | null = null;

    if (invitations.length === 1) {
        try {
            contactToShow = await contactService.getByID(
                invitations[0].contactId,
            );
        } catch (e) {
            logError(`Error while fetching contact: ${e}`);
        }
    }

    return {
        contactsCount: totalLength,
        contactToShow,
        confirmedNumber: invitations.length,
    };
}

async function calculateEventStatus(
    params: CalculateEventStatusParams,
): Promise<CalculateEventStatusResult | null> {
    const eventContactService = new EventContact({
        database: params.database,
        imageService: params.imageService,
        logDBAction: params.logger.logDBAction,
        validation: params.validation,
    });

    const contactService = new Contact({
        database: params.database,
        imageService: params.imageService,
        logger: params.logger,
        validation: params.validation,
    });

    const eventContacts = await eventContactService.fetchByParam(
        'event_id',
        params.eventId,
    );

    if (!eventContacts) {
        return null;
    }

    const awaitingResponseInvitations: EventContactModel[] = [];
    const confirmedInvitations: EventContactModel[] = [];
    const declinedInvitations: EventContactModel[] = [];

    eventContacts.forEach((eventContact) => {
        switch (eventContact.status) {
            case EVENT_CONTACT_STATUS.ACCEPTED:
                confirmedInvitations.push(eventContact);
                break;
            case EVENT_CONTACT_STATUS.REJECTED:
                declinedInvitations.push(eventContact);
                break;
            case EVENT_CONTACT_STATUS.AWAITING:
            default:
                awaitingResponseInvitations.push(eventContact);
        }
    });

    const allDeclined =
        !awaitingResponseInvitations.length &&
        !confirmedInvitations.length &&
        !!declinedInvitations.length;

    const allConfirmed =
        !awaitingResponseInvitations.length &&
        !declinedInvitations.length &&
        !!confirmedInvitations.length;

    const noneReply =
        !!awaitingResponseInvitations.length &&
        !declinedInvitations.length &&
        !confirmedInvitations.length;

    if (noneReply) {
        return null;
    }

    if (allConfirmed) {
        let contactToShow: ContactModel | null = null;

        if (confirmedInvitations.length === 1) {
            contactToShow = await contactService.getByID(
                confirmedInvitations[0].contactId,
            );
        }

        return {
            confirmed: {
                contactsCount: eventContacts.length,
                contactToShow: contactToShow,
            },
            declined: null,
            awaiting: 0,
        } as CalculateEventStatusResult;
    }

    if (allDeclined) {
        let contactToShow: ContactModel | null = null;

        if (declinedInvitations.length === 1) {
            contactToShow = await contactService.getByID(
                declinedInvitations[0].contactId,
            );
        }

        return {
            confirmed: null,
            awaiting: 0,
            declined: {
                contactsCount: eventContacts.length,
                contactToShow,
            },
        } as CalculateEventStatusResult;
    }

    const confirmedStatus = await calculateStatus(
        confirmedInvitations,
        eventContacts.length,
        contactService,
        params.logger.error,
    );
    const declinedStatus = await calculateStatus(
        declinedInvitations,
        eventContacts.length,
        contactService,
        params.logger.error,
    );

    return {
        awaiting: awaitingResponseInvitations.length,
        confirmed: confirmedStatus,
        declined: declinedStatus,
    };
}

const getReminderTextFromValue = (
    value: string | null | undefined,
    t: (scope: string, options?: { count?: number }) => string,
): string => {
    if (!value || value === USER_REMINDER.NONE) {
        return t('ShowEvent:alert:no_notification');
    }

    const timeRange = value.split('')[value.length - 1];
    const timeNumber = value.split(timeRange)[0];

    switch (timeRange) {
        case 'm':
            return t('ShowEvent:alert:m', {
                count: Number(timeNumber),
            });
        case 'h':
            return t('ShowEvent:alert:h', {
                count: Number(timeNumber),
            });
        case 'd':
            return t('ShowEvent:alert:d', {
                count: Number(timeNumber),
            });
        case 'w':
            return t('ShowEvent:alert:w', {
                count: Number(timeNumber),
            });
        default:
            return t('ShowEvent:alert:m', {
                count: Number(timeNumber),
            });
    }
};

const L = 'Event:contact_invitations:labels';

// Display buckets. Helper copy is the ticket's Confirmation column;
// PROVISIONAL entries are author-drafted, pending product sign-off.
const NEEDS_CONSENT: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_needs_consent_label`,
    helperKey: `${L}:sms_status_needs_consent_helper`,
    tone: 'warning',
};
const PREPARING: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_preparing_label`,
    helperKey: `${L}:sms_status_preparing_helper`,
    tone: 'info',
};
// Same label as PREPARING, distinct helper (table: "accepted by" vs "queued with").
const ACCEPTED: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_preparing_label`,
    helperKey: `${L}:sms_status_accepted_helper`,
    tone: 'info',
};
const SCHEDULED: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_scheduled_label`,
    helperKey: `${L}:sms_status_scheduled_helper`,
    tone: 'info',
};
const SENDING: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_sending_label`,
    helperKey: `${L}:sms_status_sending_helper`,
    tone: 'info',
};
const SENT: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_sent_label`,
    helperKey: `${L}:sms_status_sent_helper`,
    tone: 'info',
};
const DELIVERED: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_delivered_label`,
    helperKey: `${L}:sms_status_delivered_helper`,
    tone: 'success',
};
const NOT_DELIVERED: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_not_delivered_label`,
    helperKey: `${L}:sms_status_not_delivered_helper`,
    tone: 'error',
};
const CANCELED: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_canceled_label`,
    helperKey: `${L}:sms_status_canceled_helper`,
    tone: 'neutral',
}; // PROVISIONAL (canceled_opt_out)
const NOT_SENT: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_not_sent_label`,
    helperKey: `${L}:sms_status_not_sent_helper`,
    tone: 'neutral',
}; // PROVISIONAL (skipped — ticket Q-6 TBD)
const UNKNOWN: SmsStatusDisplay = {
    labelKey: `${L}:sms_status_unknown_label`,
    helperKey: `${L}:sms_status_unknown_helper`,
    tone: 'warning',
}; // delivery_unknown — defensive, currently unreachable from BE

const SMS_STATUS_DISPLAY: Record<SentSmsStatus, SmsStatusDisplay> = {
    awaiting_opt_in: NEEDS_CONSENT,
    suppressed_during_opt_out: NEEDS_CONSENT, // PROVISIONAL
    canceled_opt_out: CANCELED, // PROVISIONAL
    skipped: NOT_SENT, // PROVISIONAL
    sent_to_provider: PREPARING,
    accepted: ACCEPTED,
    scheduled: SCHEDULED,
    queued: SCHEDULED,
    sending: SENDING,
    sent: SENT,
    delivered: DELIVERED,
    read: DELIVERED, // PROVISIONAL
    undelivered: NOT_DELIVERED,
    failed: NOT_DELIVERED, // PROVISIONAL
    delivery_unknown: UNKNOWN,
};

const LEGACY_DISPLAY: Record<InvitationStatus, SmsStatusDisplay> = {
    not_sent: { labelKey: '', helperKey: '', tone: 'neutral' },
    sending: {
        labelKey: `${L}:invite_status_sending`,
        helperKey: `${L}:pending_modal_description`,
        tone: 'info',
    },
    sent: {
        labelKey: `${L}:sms_invite_status_sent`,
        helperKey: `${L}:sent_modal_description`,
        tone: 'success',
    },
};

function getSmsStatusDisplay(
    sentSmsStatus: string | null | undefined,
    sentFlag: boolean,
    sentAt: string,
    onUnknownStatus?: (status: string) => void,
): SmsStatusDisplay {
    if (sentSmsStatus) {
        const mapped = SMS_STATUS_DISPLAY[sentSmsStatus as SentSmsStatus];
        if (mapped) {
            return mapped;
        }
        // Non-null but unrecognized: report and fall through to legacy.
        onUnknownStatus?.(sentSmsStatus);
    }
    return LEGACY_DISPLAY[calculateContactInvitationState(sentFlag, sentAt)];
}

export {
    getEventCardColor,
    parseInvitations,
    calculateContactInvitationState,
    calculateEventStatus,
    getReminderTextFromValue,
    getSmsStatusDisplay,
};
